/**
 * Address formatting helpers.
 *
 * The properties.address column stores "Street + house number" together
 * (e.g. "Maximilianstraße 12a", "Hauptstr. 12-14"). For the public-facing
 * website we hide the house number — neighbours, exact location, etc. —
 * but keep the street so renters still get a useful neighbourhood signal.
 *
 * COL-218.
 */

/**
 * Strip a trailing German house number from an address string.
 *
 * Matches at the end of the string:
 *   - one or more digits          (12, 1, 123)
 *   - optional single letter      (12a, 1B)
 *   - optional range/extension    (12-14, 12/14, 1 a)
 *
 * Conservative: if nothing matches we return the input untouched so a
 * pre-redacted or non-German address ("Schloss Neuschwanstein") doesn't
 * get mangled.
 */
export function redactStreetNumber(address: string | null | undefined): string {
  if (!address) return "";
  const trimmed = address.trim();
  // Trailing whitespace + digits + optional letter, optionally followed by
  // a separator (- or /) and another digits+letter group. Examples:
  //   "Maximilianstraße 12"      → "Maximilianstraße"
  //   "Hauptstr. 12a"            → "Hauptstr."
  //   "Goethestraße 12-14"       → "Goethestraße"
  //   "Berliner Allee 1B / 2"    → "Berliner Allee"
  return trimmed.replace(
    /\s+\d+\s*[a-zA-Z]?(?:\s*[-/]\s*\d+\s*[a-zA-Z]?)?\s*$/,
    ""
  );
}

/**
 * Resolve the public-facing street label (street name, no house number) for a
 * property.
 *
 * Source of truth is the `address` column — the field the (V2) admin UI
 * actually maintains. We take its first comma segment, so a malformed full
 * address like "Hofmairstraße 37, 82327 Tutzing, Germany" still yields just the
 * street, then strip the house number.
 *
 * The legacy `street_name` column is a *contract-only* field (Straße for
 * contract documents) that the V2 admin never updates, so it silently drifts
 * from `address`. We fall back to it only when `address` is unavailable, never
 * in preference to it. (Previously street_name was preferred, which made the
 * public page show stale values — COL-218 follow-up.)
 */
export function resolvePublicStreet(
  property:
    | { address?: string | null; street_name?: string | null }
    | null
    | undefined
): string {
  const fromAddress = redactStreetNumber(
    (property?.address ?? "").split(",")[0]
  );
  if (fromAddress) return fromAddress;
  return (property?.street_name ?? "").trim();
}

/**
 * Compose the `properties.address` column ("Street + house number") from the
 * structured street fields.
 *
 * The wizard and the property edit sheet no longer let `address` be typed
 * independently — both call this whenever `street_name` or `street_number`
 * changes, instead of keeping a second manually-typed field that can silently
 * drift from the structured columns (the exact drift that caused COL-218).
 */
export function composeStreetAddress(
  streetName: string | null | undefined,
  streetNumber: string | null | undefined
): string {
  const street = (streetName ?? "").trim();
  const number = (streetNumber ?? "").trim();
  return [street, number].filter(Boolean).join(" ");
}

/**
 * Whether street_name/street_number are "half set" — exactly one of the two
 * has a value while the other is blank.
 *
 * composeStreetAddress happily derives an address from just one side ("12"
 * with no street, or a street with no house number), which is the right
 * building block for a live-typing field, but it's a degraded address, not a
 * fit state to save: the wizard already requires both together before a
 * property can be created (validateWizardStep step 1). The edit sheet has no
 * such gate on an existing property, so without this check clearing just one
 * side would silently overwrite a previously complete address with a
 * fragment (COL-460).
 */
export function isPartialStreetAddress(
  streetName: string | null | undefined,
  streetNumber: string | null | undefined
): boolean {
  const hasStreet = !!(streetName ?? "").trim();
  const hasNumber = !!(streetNumber ?? "").trim();
  return hasStreet !== hasNumber;
}

/**
 * Error shown when a street name ends in an abbreviated "Straße"/"Strasse" —
 * the single message all three admin surfaces (Add wizard, Edit sheet, legacy
 * contract fields) use.
 */
export const STREET_NAME_ABBREVIATION_ERROR =
  'Write the street out in full — no "." allowed. Use "Teststrasse", not "Teststr.".';

/** Matches a trailing abbreviated "Straße"/"Strasse", e.g. "Teststr." or "Muster Str." */
const ABBREVIATED_STRASSE = /str\.\s*$/i;

/**
 * Reject abbreviated street names.
 *
 * `properties.street_name` is printed verbatim on official documents — the
 * Wohnungsgeberbestätigung a tenant hands to the Meldebehörde, and the rental
 * contract — where an abbreviation like "Teststr." is not acceptable input.
 * There is no auto-expansion here on purpose: "Str." can be Straße/Strasse and
 * a machine guess on a legal document is worse than making staff type it.
 *
 * Only a trailing "str." is flagged — not every "." in the value. A blanket
 * "any dot" rule also rejects real, fully-spelled German street names that
 * use "." for "Sankt" ("Saint"), e.g. Munich's own St.-Anna-Straße — those
 * are not abbreviations of Straße and must be accepted.
 *
 * @returns the error message, or null when the value is acceptable.
 */
export function validateStreetName(
  value: string | null | undefined
): string | null {
  const trimmed = (value ?? "").trim();
  return ABBREVIATED_STRASSE.test(trimmed)
    ? STREET_NAME_ABBREVIATION_ERROR
    : null;
}
